/** Browser paths carried through the OIDC user state, never an external URL. */
export interface SigninLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export interface SigninReturnState {
  readonly returnTo: string;
}

function safeReturnPath(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return "/";
  }
  try {
    const decoded = decodeURIComponent(value);
    if (
      decoded.startsWith("//") ||
      decoded.includes("\\") ||
      [...decoded].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    ) {
      return "/";
    }
    // Normalize the original URL before decoding its pathname. Decoding the
    // whole URL first would turn encoded ?/# into delimiters and hide later
    // dot segments (for example /a%3f/../callback).
    const path = decodeURIComponent(new URL(value, "https://signin.invalid").pathname);
    const decodedPath = new URL(decoded, "https://signin.invalid").pathname;
    if ([path, decodedPath].some((candidate) => /^\/(?:callback|login|logout)(?:\/|$)/i.test(candidate))) return "/";
    return value;
  } catch {
    return "/";
  }
}

export function createSigninReturnState(location: SigninLocation): SigninReturnState {
  return { returnTo: safeReturnPath(`${location.pathname}${location.search}${location.hash}`) };
}

export function readSigninReturnTo(state: unknown): string {
  return safeReturnPath(
    state !== null && typeof state === "object" && "returnTo" in state ? state.returnTo : undefined,
  );
}
